function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Autocomplete as MUIAutocomplete } from '@material-ui/lab';
import { InputAdornment, InputLabel, ListItemText, TextField, makeStyles } from '@material-ui/core';
import { Search as SearchIcon } from '@material-ui/icons';
import identity from 'lodash.identity';
import uniqueId from 'lodash.uniqueid';
import useFormHelpers from '../../hooks/useFormHelpers';
const useStyles = makeStyles(theme => ({
  autocomplete: {
    width: '100%',
    // ensures full-width in all contexts, e.g. Dialogs
    '& *': {
      color: 'inherit'
    }
  },
  label: {
    height: 1,
    left: -10000,
    overflow: 'hidden',
    position: 'absolute',
    top: 'auto',
    width: 1
  }
}));

const Autocomplete = ({
  autoFocus,
  backgroundColor,
  className,
  error,
  getOptionLabel,
  getOptionSecondaryText,
  getOptionValue,
  helperText,
  icon,
  name,
  onChange,
  options,
  placeholder,
  ...props
}) => {
  const [inputId] = useState(() => uniqueId('autocomplete_'));
  const classes = useStyles();
  return React.createElement(MUIAutocomplete, _extends({
    className: `${classes.autocomplete} ${className ? className : ''}`,
    id: inputId,
    clearOnEscape: true,
    disableOpenOnFocus: true,
    onChange: (e, option) => {
      onChange(option ? getOptionValue(option) : '');
    },
    options: options,
    renderInput: params => React.createElement(React.Fragment, null, React.createElement(InputLabel, {
      htmlFor: inputId,
      error: error,
      className: classes.label
    }, placeholder), React.createElement(TextField, _extends({}, params, {
      InputProps: { ...params.InputProps,
        autoFocus,
        name,
        startAdornment: // pointerEvents need to be set to none to allow clicks to pass through the icon
        // https://github.com/sporkbytes/front-end-react/pull/6#pullrequestreview-359905596
        React.createElement(InputAdornment, {
          position: "start",
          style: {
            pointerEvents: 'none'
          }
        }, icon),
        style: {
          backgroundColor
        }
      },
      variant: "outlined",
      placeholder: placeholder,
      helperText: helperText,
      error: error,
      fullWidth: true
    }))),
    renderOption: option => React.createElement(ListItemText, {
      primary: getOptionLabel(option),
      secondary: getOptionSecondaryText(option)
    }),
    getOptionLabel: getOptionLabel
  }, props));
};

Autocomplete.propTypes = {
  autoFocus: PropTypes.bool,
  backgroundColor: PropTypes.string,
  error: PropTypes.bool,
  getOptionLabel: PropTypes.func.isRequired,
  // this must be defined in order to avoid errors when selecting an option
  getOptionSecondaryText: PropTypes.func,
  getOptionValue: PropTypes.func,
  helperText: PropTypes.string,
  icon: PropTypes.element,
  name: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.array,
  placeholder: PropTypes.string
};
Autocomplete.defaultProps = {
  autoFocus: false,
  backgroundColor: 'white',
  getOptionSecondaryText: () => undefined,
  getOptionValue: identity,
  icon: React.createElement(SearchIcon, null),
  placeholder: 'Search...'
};

const LinkedAutocomplete = props => {
  // value must be removed from props since Autocomplete is uncontrolled
  const {
    value,
    ...helpers
  } = useFormHelpers(props);
  return React.createElement(Autocomplete, helpers);
};

LinkedAutocomplete.propTypes = {
  name: PropTypes.string.isRequired
};
export { Autocomplete, LinkedAutocomplete };